// src/asset/AssetsRegistry.js
// Centralized helpers for AssetEntries:
//  - AssetEntries (DB rows)
//  - JSON files in S3
//  - event payloads for DataStore / OZ
//
// SINGLE SOURCE OF TRUTH:
//   - AssetEntries from DataStore / GraphQL subscription
//   - JSON content in S3 referenced by those AssetEntries
//
// CACHES:
//   - assetEntryIndex: in-memory index built ONLY from subscription-driven AssetEntries
//   - assetContentCache: lazy read-through cache of S3 JSON
//
// IMPORTANT: There is NO save-time mutation of caches.
//            All invalidation happens when the AssetEntries array changes and
//            initAssetRegistry(...) is called from App.js.
//
// STRICT RULE (NO FALLBACKS):
//   - All stored/transported S3 locations MUST be full S3 URIs:
//       s3://<AssetsBucket>/<projectToken>/<key>_<LANG>.json
//   - We DO NOT accept plain object keys in AssetEntry.s3Key.
//   - We DO NOT derive object keys from accountToken.
//   - Any mismatch throws early to avoid silent corruption.

import { fetchAuthSession } from 'aws-amplify/auth';
import { S3Client, GetObjectCommand, PutObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { AssetsBucket } from '../Enums.js';

// ---------------------------------------------------------------------------
// Minimal logging helper
// ---------------------------------------------------------------------------

function debugLog(label, payload) {
  // eslint-disable-next-line no-console
  console.log(`[AssetsRegistry] ${label}`, payload);
}

// ---------------------------------------------------------------------------
// In-memory registries
// ---------------------------------------------------------------------------

// key: assetKey, value: AssetEntry (DB row)
const assetEntryIndex = new Map();

// key: assetKey, value: { json, s3Uri, updatedAt }
const assetContentCache = new Map();

// Bucket used for OPTIONS/DICTIONARY assets.
// This comes from Enums.js and is environment-specific (dev/staging/prod).
const bucket = AssetsBucket;

// Default region for assets (adjust if needed)
const DEFAULT_REGION = 'eu-west-1';

// ---------------------------------------------------------------------------
// Registry init / lookup
// ---------------------------------------------------------------------------

/**
 * Initialize / refresh the AssetEntry index from an array of AssetEntries.
 * Call this from App.js whenever your DataStore AssetEntries change.
 *
 * SIDE EFFECT:
 *   - Clears assetEntryIndex
 *   - Clears assetContentCache (forces S3 re-read on next getAssetJson)
 */
export function initAssetRegistry(assetEntries) {
  assetEntryIndex.clear();
  assetContentCache.clear();

  if (!Array.isArray(assetEntries)) {
    debugLog('initAssetRegistry', {
      count: 0,
      bucket,
      note: 'assetEntries not an array; registry cleared',
    });
    return;
  }

  for (const entry of assetEntries) {
    if (entry && entry.key) {
      assetEntryIndex.set(entry.key, entry);
    }
  }

  debugLog('initAssetRegistry', {
    count: assetEntryIndex.size,
    bucket,
    cacheCleared: true,
  });
}

/**
 * Check if the registry has any AssetEntries loaded.
 */
export function hasAssetEntries() {
  return assetEntryIndex.size > 0;
}

/**
 * Get a single AssetEntry by key (and optional language) from the registry.
 *
 * language:
 *  - default is "EN" when not explicitly overridden
 *  - matching is case-insensitive
 *  - if language is null, language is ignored for matching
 */
export function getAssetEntryByKey(key, language = 'EN') {
  if (!key) {
    // eslint-disable-next-line no-console
    console.log('[AssetsRegistry] getAssetEntryByKey', {
      key,
      requestedLanguage: language ?? null,
      found: false,
      reason: 'missing-key',
    });
    return null;
  }

  const entry = assetEntryIndex.get(key) || null;
  if (!entry) {
    return null;
  }

  const requestedLang = language != null ? String(language).toUpperCase() : null;
  const entryLang = entry.language != null ? String(entry.language).toUpperCase() : null;

  const matchesLanguage =
    !requestedLang || !entryLang || entryLang === requestedLang;

  if (!matchesLanguage) {
    return null;
  }

  return entry;
}

/**
 * Clear all AssetEntries and cached JSON.
 * (Useful on logout / account switch.)
 */
export function clearAssetRegistry() {
  assetEntryIndex.clear();
  assetContentCache.clear();
}

// ---------------------------------------------------------------------------
// S3 client / helpers
// ---------------------------------------------------------------------------

async function getAwsCredentials() {
  const session = await fetchAuthSession();
  return { credentials: session?.credentials ?? null };
}

async function getS3ClientForAssets() {
  const { credentials } = await getAwsCredentials();
  if (!credentials) {
    throw new Error('getS3ClientForAssets: missing AWS credentials');
  }
  return new S3Client({
    region: DEFAULT_REGION,
    credentials,
  });
}

// ---------------------------------------------------------------------------
// S3 URI helpers (STRICT: URI everywhere)
// ---------------------------------------------------------------------------

function parseS3Uri(uri) {
  const m = typeof uri === 'string' && uri.match(/^s3:\/\/([^/]+)\/(.+)$/i);
  return m ? { bucket: m[1], key: m[2] } : { bucket: null, key: null };
}

function assertS3UriForAssets(s3Uri, label) {
  if (!s3Uri || typeof s3Uri !== 'string') {
    throw new Error(`${label}: s3Uri is required`);
  }
  if (!s3Uri.startsWith('s3://')) {
    throw new Error(`${label}: s3Uri must start with "s3://" (got "${s3Uri}")`);
  }

  const parsed = parseS3Uri(s3Uri);
  if (!parsed.bucket || !parsed.key) {
    throw new Error(`${label}: invalid s3Uri "${s3Uri}"`);
  }

  if (parsed.bucket !== bucket) {
    throw new Error(
      `${label}: s3Uri bucket mismatch. Expected "${bucket}" but got "${parsed.bucket}" (${s3Uri})`
    );
  }

  return parsed; // { bucket, key }
}

/**
 * Canonical object key for AssetsBucket.
 * Result:
 *   <projectToken>/<key>[_LANG].json
 */
export function deriveS3ObjectKey({ projectToken, key, language }) {
  if (!projectToken) {
    throw new Error('deriveS3ObjectKey: projectToken is required');
  }
  if (!key) {
    throw new Error('deriveS3ObjectKey: key is required');
  }

  const langCode = (language || 'EN').toUpperCase();
  const langSuffix = langCode ? `_${langCode}` : '';
  return `${projectToken}/${key}${langSuffix}.json`;
}

/**
 * Canonical S3 URI for AssetsBucket (THIS is what you store/transport).
 * Result:
 *   s3://<AssetsBucket>/<projectToken>/<key>[_LANG].json
 */
export function deriveS3Uri({ projectToken, key, language }) {
  const objectKey = deriveS3ObjectKey({ projectToken, key, language });
  return `s3://${bucket}/${objectKey}`;
}

// ---------------------------------------------------------------------------
// Hash helpers (SHA-256)
// ---------------------------------------------------------------------------

/**
 * Compute SHA-256 hash of a string and return lowercase hex string.
 * Semantics are equivalent to:
 *   Get-FileHash -Algorithm SHA256 | .Hash.ToLower()
 */
export async function computeSHA256Hex(str) {
  if (
    typeof crypto === 'undefined' ||
    !crypto.subtle ||
    typeof crypto.subtle.digest !== 'function'
  ) {
    return null;
  }

  const enc = new TextEncoder();
  const data = enc.encode(str);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(digest);
  let hex = '';

  for (let i = 0; i < bytes.length; i += 1) {
    const b = bytes[i].toString(16).padStart(2, '0');
    hex += b;
  }

  return hex.toLowerCase();
}

async function computeContentHashForJson(jsonString) {
  if (typeof jsonString !== 'string') {
    throw new Error('computeContentHashForJson: jsonString must be a string');
  }

  const hash = await computeSHA256Hex(jsonString);
  if (!hash) {
    throw new Error('computeContentHashForJson: unable to compute SHA-256 hash');
  }

  return hash;
}

// ---------------------------------------------------------------------------
// AssetEntry lookup helper (for save paths)
// ---------------------------------------------------------------------------

/**
 * Locate an AssetEntry from the internal registry only.
 *
 * Language matching is delegated to getAssetEntryByKey.
 */
export function findAssetEntry({ key, language }) {
  if (!key) return null;
  return getAssetEntryByKey(key, language);
}

// ---------------------------------------------------------------------------
// Event payload builder
// ---------------------------------------------------------------------------

/**
 * Build a generic payload for an asset upsert.
 *
 * STRICT:
 *  - s3Uri MUST be a full s3:// URI in the AssetsBucket (no plain keys).
 *  - contentHash MUST be a non-empty string.
 *  - uploadedBy MUST be a non-empty string.
 *  - updatedAt MUST be an ISO string.
 */
export function buildAssetEventPayload({
  accountToken,
  projectToken,
  formToken,
  key,
  language,
  name,
  description,
  type,
  s3Uri,
  fileSize,
  contentHash,
  isNew,
  assetEntry,
  userId,
  uploadedBy,
  updatedAt,
}) {
  if (!contentHash || typeof contentHash !== 'string') {
    throw new Error(
      `buildAssetEventPayload: contentHash is required and must be a non-empty string for asset "${key}"`
    );
  }

  if (!uploadedBy || typeof uploadedBy !== 'string') {
    throw new Error(
      `buildAssetEventPayload: uploadedBy is required and must be a non-empty string for asset "${key}"`
    );
  }

  if (!updatedAt || typeof updatedAt !== 'string') {
    throw new Error(
      `buildAssetEventPayload: updatedAt is required and must be an ISO string for asset "${key}"`
    );
  }

  assertS3UriForAssets(s3Uri, `buildAssetEventPayload: asset "${key}"`);

  return {
    eventType: 'ASSET_UPSERT',
    action: isNew ? 'CREATE' : 'UPDATE',
    assetKey: key,
    accountToken,
    projectToken,
    formToken,
    language,
    name,
    description,
    assetType: type,

    // IMPORTANT: event field name remains "s3Key" for downstream compatibility,
    // but the value is ALWAYS a full S3 URI.
    s3Key: s3Uri,

    fileSize,
    contentHash,
    updatedAt,
    userId: userId ?? null,
    uploadedBy,
    existingAssetId: assetEntry?.id || null,
  };
}

// ---------------------------------------------------------------------------
// Internal helper: normalize updatedAssetsMap entry (no legacy support)
// ---------------------------------------------------------------------------

/**
 * REQUIRED SHAPE (no legacy support):
 *   updatedAssetsMap[key] = {
 *     content: <json>,
 *     type:    <string>,   // enum, non-empty
 *     language?: <string>, // defaults to "EN"
 *     name?: <string>,
 *     description?: <string>,
 *   }
 */
function normalizeUpdatedAssetValue(value, assetKey) {
  if (!value || typeof value !== 'object') {
    throw new Error(
      `normalizeUpdatedAssetValue: updatedAssetsMap["${assetKey}"] must be an object { content, type, language?, name?, description? }`
    );
  }

  if (!Object.prototype.hasOwnProperty.call(value, 'content')) {
    throw new Error(
      `normalizeUpdatedAssetValue: updatedAssetsMap["${assetKey}"] is missing "content"`
    );
  }

  const { content, type, language, name, description } = value;

  if (!content || typeof content !== 'object') {
    throw new Error(
      `normalizeUpdatedAssetValue: "content" for asset "${assetKey}" must be a non-null object/array`
    );
  }

  if (!type || typeof type !== 'string') {
    throw new Error(
      `normalizeUpdatedAssetValue: "type" is required and must be a non-empty string for asset "${assetKey}"`
    );
  }

  const effectiveLanguage = (language || 'EN').toUpperCase();

  return {
    json: content,
    meta: {
      type,
      language: effectiveLanguage,
      name: name || null,
      description: description || null,
    },
  };
}

// ---------------------------------------------------------------------------
// Plan builders (single + batch)
// ---------------------------------------------------------------------------

export async function prepareOptionsAssetUpdate({
  assetKey,
  content,
  accountToken,
  projectToken,
  formToken,
  language,
  name,
  description,
  type,
  userId = null,
  uploadedBy,
  computeHash = false,
  updatedAt,
}) {
  if (!assetKey) {
    throw new Error('prepareOptionsAssetUpdate: assetKey is required');
  }
  if (!content || typeof content !== 'object') {
    throw new Error(
      'prepareOptionsAssetUpdate: content (full JSON object/array) is required'
    );
  }
  if (!accountToken) {
    throw new Error('prepareOptionsAssetUpdate: accountToken is required');
  }
  if (!projectToken) {
    throw new Error('prepareOptionsAssetUpdate: projectToken is required');
  }
  if (!type) {
    throw new Error(
      `prepareOptionsAssetUpdate: type is required for asset "${assetKey}"`
    );
  }
  if (!uploadedBy) {
    throw new Error(
      `prepareOptionsAssetUpdate: uploadedBy is required for asset "${assetKey}"`
    );
  }
  if (!updatedAt) {
    throw new Error(
      `prepareOptionsAssetUpdate: updatedAt is required for asset "${assetKey}"`
    );
  }

  const assetEntry = findAssetEntry({ key: assetKey, language });
  const isNew = !assetEntry;

  const effectiveLanguage = (language || 'EN').toUpperCase();

  // Canonical identifiers:
  const objectKey = deriveS3ObjectKey({
    projectToken,
    key: assetKey,
    language: effectiveLanguage,
  });

  const s3Uri = deriveS3Uri({
    projectToken,
    key: assetKey,
    language: effectiveLanguage,
  });

  const jsonString = JSON.stringify(content, null, 2);
  const fileSize = jsonString.length;

  const contentHash = computeHash ? await computeContentHashForJson(jsonString) : null;
  if (!contentHash) {
    throw new Error(
      `prepareOptionsAssetUpdate: contentHash is required for asset "${assetKey}" (computeHash must be true)`
    );
  }

  const eventPayload = buildAssetEventPayload({
    accountToken,
    projectToken,
    formToken,
    key: assetKey,
    language: effectiveLanguage,
    name,
    description,
    type,
    s3Uri,
    fileSize,
    contentHash,
    isNew,
    assetEntry,
    userId,
    uploadedBy,
    updatedAt,
  });

  return {
    assetKey,
    isNew,
    objectKey, // used ONLY for S3 SDK writes
    s3Uri, // stored/transported ONLY as URI
    jsonString,
    fileSize,
    contentHash,
    assetEntry,
    eventPayload,
  };
}

export async function prepareAssetsBatchUpdate({
  updatedAssetsMap,
  accountToken,
  projectToken,
  formToken,
  computeHash = false,
  defaultUploadedBy,
  defaultUserId = null,
}) {
  if (!updatedAssetsMap || typeof updatedAssetsMap !== 'object') {
    return [];
  }

  const keys = Object.keys(updatedAssetsMap);
  if (!keys.length) {
    return [];
  }

  if (!accountToken) {
    throw new Error('prepareAssetsBatchUpdate: accountToken is required');
  }
  if (!projectToken) {
    throw new Error('prepareAssetsBatchUpdate: projectToken is required');
  }
  if (!formToken) {
    throw new Error('prepareAssetsBatchUpdate: formToken is required');
  }
  if (!defaultUploadedBy) {
    throw new Error(
      'prepareAssetsBatchUpdate: defaultUploadedBy (current user initials) is required'
    );
  }

  const updatedAt = new Date().toISOString();
  const plans = [];

  for (const [assetKey, rawValue] of Object.entries(updatedAssetsMap)) {
    const { json: content, meta: mapMeta } = normalizeUpdatedAssetValue(rawValue, assetKey);

    const assetEntry = findAssetEntry({
      key: assetKey,
      language: mapMeta.language,
    });

    const isNew = !assetEntry;

    const name =
      mapMeta.name != null
        ? mapMeta.name
        : isNew
          ? assetKey
          : assetEntry?.name || assetKey;

    const description =
      mapMeta.description != null
        ? mapMeta.description
        : isNew
          ? `Asset ${assetKey}`
          : assetEntry?.description || `Asset ${assetKey}`;

    const plan = await prepareOptionsAssetUpdate({
      assetKey,
      content,
      accountToken,
      projectToken,
      formToken,
      language: mapMeta.language,
      name,
      description,
      type: mapMeta.type,
      userId: defaultUserId,
      uploadedBy: defaultUploadedBy,
      computeHash,
      updatedAt,
    });

    plans.push(plan);
  }

  return plans;
}

/**
 * Execute an array of plans.
 * `emitEvent` is optional; if omitted, eventResult will be null.
 */
export async function executeAssetPlans({ plans, putToS3, emitEvent }) {
  if (!Array.isArray(plans) || plans.length === 0) {
    return [];
  }

  if (typeof putToS3 !== 'function') {
    throw new Error('executeAssetPlans: putToS3 must be provided');
  }

  const results = [];

  for (const plan of plans) {
    const { objectKey, jsonString, eventPayload, assetKey, s3Uri } = plan;

    await putToS3({ objectKey, body: jsonString, assetKey });

    let eventResult = null;
    if (typeof emitEvent === 'function' && eventPayload) {
      eventResult = await emitEvent(eventPayload);
    }

    results.push({
      assetKey,
      s3Uri,
      eventResult,
    });
  }

  return results;
}

// ---------------------------------------------------------------------------
// Presigned fetch helpers (URI only)
// ---------------------------------------------------------------------------

async function safeText(resp) {
  try {
    return await resp.text();
  } catch {
    return '';
  }
}

function sniffRegionFromXml(xml) {
  if (!xml) return null;
  const m1 = xml.match(/<Region>([^<]+)<\/Region>/i);
  return m1 ? m1[1] : null;
}

/** Minimal presign + fetch (JSON) with one retry on region mismatch. */
async function fetchJsonFromS3Uri(s3Uri) {
  const parsed = assertS3UriForAssets(s3Uri, 'fetchJsonFromS3Uri');
  const { bucket: uriBucket, key } = parsed;

  const { credentials } = await getAwsCredentials();
  if (!credentials) {
    throw new Error('fetchJsonFromS3Uri: missing AWS credentials');
  }

  let region = DEFAULT_REGION;

  const getWithRegion = async (rgn) => {
    const s3 = new S3Client({ region: rgn, credentials });
    const url = await getSignedUrl(
      s3,
      new GetObjectCommand({ Bucket: uriBucket, Key: key }),
      { expiresIn: 60 }
    );
    const resp = await fetch(url);
    return { resp };
  };

  let { resp } = await getWithRegion(region);
  if (!resp.ok) {
    const body = await safeText(resp);
    const hinted = sniffRegionFromXml(body);
    if (hinted && hinted !== region) {
      region = hinted;
      ({ resp } = await getWithRegion(region));
    }
  }

  if (!resp.ok) {
    const body = await safeText(resp);
    throw new Error(
      `fetchJsonFromS3Uri: ${resp.status} ${resp.statusText} for ${s3Uri} :: ${body?.slice(0, 256)}`
    );
  }

  return await resp.json();
}

async function fetchObjectResponseFromS3Uri(s3Uri) {
  const parsed = assertS3UriForAssets(s3Uri, 'fetchObjectResponseFromS3Uri');
  const { bucket: uriBucket, key } = parsed;

  const { credentials } = await getAwsCredentials();
  if (!credentials) {
    throw new Error('fetchObjectResponseFromS3Uri: missing AWS credentials');
  }

  const s3 = new S3Client({ region: DEFAULT_REGION, credentials });

  const url = await getSignedUrl(
    s3,
    new GetObjectCommand({ Bucket: uriBucket, Key: key }),
    { expiresIn: 60 }
  );

  const resp = await fetch(url);

  if (!resp.ok) {
    const body = await safeText(resp);
    throw new Error(
      `fetchObjectResponseFromS3Uri: ${resp.status} ${resp.statusText} for ${s3Uri} :: ${body?.slice(0, 256)}`
    );
  }

  return resp;
}

// ---------------------------------------------------------------------------
// Content cache API (JSON from S3) – URI only
// ---------------------------------------------------------------------------

/**
 * Lazy, cached fetch of asset JSON from S3 using the registry.
 *
 * STRICT:
 *  - AssetEntry.s3Key MUST be a full s3:// URI (and must point at AssetsBucket).
 *  - No plain-key support. No derivation fallback.
 */
export async function getAssetJson({ assetKey }) {
  if (!assetKey) {
    throw new Error('getAssetJson: assetKey is required');
  }

  const cached = assetContentCache.get(assetKey);
  if (cached) {
    return cached.json;
  }

  const assetEntry = getAssetEntryByKey(assetKey, null);
  if (!assetEntry) {
    throw new Error(`getAssetJson: AssetEntry not found for key ${assetKey}`);
  }

  const s3Uri = assetEntry.s3Key;
  assertS3UriForAssets(s3Uri, `getAssetJson: AssetEntry.s3Key for "${assetKey}"`);

  const json = await fetchJsonFromS3Uri(s3Uri);

  assetContentCache.set(assetKey, {
    json,
    s3Uri,
    updatedAt: Date.now(),
  });

  return json;
}

/**
 * DEPRECATED: manual JSON cache mutation.
 * This is now a no-op to enforce that cache is only driven by subscription
 * changes via initAssetRegistry(...) + getAssetJson().
 */
export function updateAssetJsonCache(assetKey, json) {
  if (!assetKey || !json) return;
  // eslint-disable-next-line no-console
  console.warn(
    '[AssetsRegistry] updateAssetJsonCache is deprecated; cache is driven by AssetEntries subscription only.'
  );
}

/**
 * Optional: warm the cache for a set of asset keys.
 */
export async function warmAssets({ assetKeys }) {
  if (!Array.isArray(assetKeys) || assetKeys.length === 0) return;

  for (const key of assetKeys) {
    try {
      await getAssetJson({ assetKey: key });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('warmAssets: failed to warm asset', key, err);
    }
  }
}

/**
 * Clear only the JSON cache (leave AssetEntries intact).
 */
export function clearAssetCache() {
  assetContentCache.clear();
}

// ---------------------------------------------------------------------------
// High-level save entrypoint used by App.js (URI stored, objectKey used)
// ---------------------------------------------------------------------------

export async function saveAssetsBatch({
  updatedAssetsMap,
  accountToken,
  projectToken,
  formToken,
  DataStore,
  defaultLanguage = null,
  defaultUploadedBy,
  defaultUserId = null,
}) {
  const keys =
    updatedAssetsMap && typeof updatedAssetsMap === 'object'
      ? Object.keys(updatedAssetsMap)
      : [];

  if (!accountToken) {
    throw new Error('saveAssetsBatch: accountToken is required');
  }
  if (!projectToken) {
    throw new Error('saveAssetsBatch: projectToken is required');
  }
  if (!formToken) {
    throw new Error('saveAssetsBatch: formToken is required for this flow');
  }
  if (!defaultUploadedBy) {
    throw new Error(
      'saveAssetsBatch: defaultUploadedBy (current user initials) is required'
    );
  }
  if (!defaultUserId) {
    throw new Error('saveAssetsBatch: defaultUserId (current user id) is required');
  }

  const plans = await prepareAssetsBatchUpdate({
    updatedAssetsMap,
    accountToken,
    projectToken,
    formToken,
    computeHash: true,
    defaultUploadedBy,
    defaultUserId,
  });

  const putToS3 = async ({ objectKey, body, assetKey }) => {
    const s3 = await getS3ClientForAssets();

    try {
      const result = await s3.send(
        new PutObjectCommand({
          Bucket: bucket,
          Key: objectKey,
          Body: body,
          ContentType: 'application/json',
        })
      );

      return result;
    } catch (err) {
      debugLog('putToS3:error', {
        assetKey,
        bucket,
        objectKey,
        name: err?.name,
        message: err?.message || String(err),
      });
      throw err;
    }
  };

  const results = await executeAssetPlans({
    plans,
    putToS3,
  });

  const upsertAssets = plans.map((plan) => {
    const {
      assetKey,
      isNew,
      s3Uri,
      fileSize,
      contentHash,
      assetEntry,
      eventPayload: builderProps,
    } = plan;

    const operation = isNew ? 'create' : 'update';

    const type = builderProps?.assetType;
    if (!type) {
      throw new Error(
        `saveAssetsBatch: missing type (builderProps.assetType) for asset "${assetKey}".`
      );
    }

    const name = builderProps?.name;
    if (!name) {
      throw new Error(
        `saveAssetsBatch: missing name (builderProps.name) for asset "${assetKey}".`
      );
    }

    const description = builderProps?.description || `Asset ${assetKey}`;
    const language = builderProps?.language || 'EN';

    let id;
    let concurrencyVersion;
    let businessVersion;

    if (isNew) {
      id = undefined;
      concurrencyVersion = undefined;
      businessVersion = 1;
    } else {
      if (!assetEntry || !assetEntry.id) {
        throw new Error(
          `saveAssetsBatch: update requested for asset "${assetKey}" but no existing AssetEntry with id.`
        );
      }
      if (typeof assetEntry._version !== 'number') {
        throw new Error(
          `saveAssetsBatch: update requested for asset "${assetKey}" but missing _version on AssetEntry.`
        );
      }
      if (typeof assetEntry.version !== 'number') {
        throw new Error(
          `saveAssetsBatch: update requested for asset "${assetKey}" but missing business version on AssetEntry.`
        );
      }

      id = assetEntry.id;
      concurrencyVersion = assetEntry._version;
      businessVersion = assetEntry.version + 1;
    }

    const now = new Date().toISOString();
    const uploadedAt = now;
    const updatedAt = now;
    const fileType = 'JSON';

    // IMPORTANT:
    // The API/model field is called "s3Key" but we store a full S3 URI in it.
    const assetPayload = {
      ...(id ? { id } : {}),
      ...(!isNew && typeof concurrencyVersion === 'number' ? { _version: concurrencyVersion } : {}),
      accountToken,
      projectToken,
      formToken,
      userId: defaultUserId || null,
      key: assetKey,
      type,
      name,
      description,
      language,
      s3Key: s3Uri,
      fileType,
      fileSize,
      contentHash,
      version: businessVersion,
      uploadedAt,
      uploadedBy: defaultUploadedBy,
      updatedAt,
    };

    assertS3UriForAssets(assetPayload.s3Key, `saveAssetsBatch: asset "${assetKey}" assetPayload.s3Key`);

    return { operation, assetPayload };
  });

  void results;
  void keys;
  void DataStore;
  void defaultLanguage;

  return upsertAssets;
}

/**
 * Return all AssetEntries currently in the in-memory registry.
 * Useful for admin/debug UIs like the AssetIndexDataGrid.
 */
export function getAllAssetEntries() {
  return Array.from(assetEntryIndex.values());
}

// ---------------------------------------------------------------------------
// S3 helpers for downloading asset files (URI only)
// ---------------------------------------------------------------------------

export async function downloadAssetBlobFromS3Uri(s3Uri) {
  assertS3UriForAssets(s3Uri, 'downloadAssetBlobFromS3Uri');
  const resp = await fetchObjectResponseFromS3Uri(s3Uri);
  return await resp.blob();
}

/**
 * Download an asset blob by AssetKey.
 *
 * STRICT:
 *  - AssetEntry.s3Key MUST be a full s3:// URI in AssetsBucket.
 *  - No plain-key support. No derivation fallback.
 */
export async function downloadAssetBlobFromAssetKey(assetKey) {
  if (!assetKey) {
    throw new Error('downloadAssetBlobFromAssetKey: assetKey is required');
  }

  const assetEntry = getAssetEntryByKey(assetKey, null);
  if (!assetEntry) {
    throw new Error(`downloadAssetBlobFromAssetKey: missing AssetEntry for ${assetKey}`);
  }

  const s3Uri = assetEntry.s3Key;
  assertS3UriForAssets(s3Uri, `downloadAssetBlobFromAssetKey: AssetEntry.s3Key for "${assetKey}"`);

  return await downloadAssetBlobFromS3Uri(s3Uri);
}